import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ChatError, ChatErrorType } from '../core/chat.types';

/**
 * ErrorHandler - Centralized error handling for chat operations
 *
 * This service handles:
 * - Error classification based on HTTP status codes and error types
 * - Error formatting for user-friendly display
 * - Error recovery strategies and retry logic
 * - Error reporting and logging
 */
@Injectable({
  providedIn: 'root',
})
export class ChatErrorHandler {
  /**
   * Handle and transform any error into a standardized ChatError
   */
  handleError(error: any): ChatError {
    const errorType = this.classifyError(error);
    const message = this.extractErrorMessage(error);
    const retryable = this.isRetryableError({ type: errorType } as ChatError);

    const chatError: ChatError = {
      type: errorType,
      message,
      details: this.extractErrorDetails(error),
      timestamp: Date.now(),
      retryable
    };

    // Log error for debugging
    console.error('ChatErrorHandler: Error occurred', chatError);

    return chatError;
  }

  /**
   * Classify error type based on error properties
   */
  classifyError(error: any): ChatErrorType {
    // Check for HTTP error responses
    if (error?.status !== undefined) {
      const status = error.status;
      
      // Authentication errors
      if (status === 401 || status === 403) {
        return ChatErrorType.AUTHENTICATION_ERROR;
      }
      
      // Rate limit errors
      if (status === 429) {
        return ChatErrorType.RATE_LIMIT_ERROR;
      }
      
      // Client errors (4xx)
      if (status >= 400 && status < 500) {
        return ChatErrorType.VALIDATION_ERROR;
      }
      
      // Server errors (5xx)
      if (status >= 500) {
        return ChatErrorType.UNKNOWN_ERROR;
      }
      
      // Network errors (status 0)
      if (status === 0) {
        return ChatErrorType.NETWORK_ERROR;
      }
    }

    // Check for specific error types
    if (error?.name === 'HttpErrorResponse' || error?.message?.toLowerCase().includes('network')) {
      return ChatErrorType.NETWORK_ERROR;
    }

    // Check for stream-specific errors
    if (error?.message?.toLowerCase().includes('stream')) {
      return ChatErrorType.STREAM_ERROR;
    }

    // Check for timeout errors
    if (error?.name === 'TimeoutError' || error?.message?.toLowerCase().includes('timeout')) {
      return ChatErrorType.NETWORK_ERROR;
    }

    // Default to unknown error
    return ChatErrorType.UNKNOWN_ERROR;
  }

  /**
   * Format error for user-friendly display
   */
  formatErrorForDisplay(error: ChatError): string {
    switch (error.type) {
      case ChatErrorType.NETWORK_ERROR:
        return 'Unable to connect to the server. Please check your internet connection and try again.';
      
      case ChatErrorType.AUTHENTICATION_ERROR:
        return 'Authentication failed. Please log in again to continue.';
      
      case ChatErrorType.RATE_LIMIT_ERROR:
        return 'You\'ve made too many requests. Please wait a moment and try again.';
      
      case ChatErrorType.VALIDATION_ERROR:
        return error.message || 'Invalid request. Please check your input and try again.';
      
      case ChatErrorType.STREAM_ERROR:
        return 'Connection interrupted. The response may be incomplete.';
      
      case ChatErrorType.UNKNOWN_ERROR:
      default:
        return error.message || 'An unexpected error occurred. Please try again later.';
    }
  }

  /**
   * Check if error can be retried
   */
  isRetryableError(error: ChatError): boolean {
    // These error types are typically retryable
    const retryableTypes = [
      ChatErrorType.NETWORK_ERROR,
      ChatErrorType.RATE_LIMIT_ERROR,
      ChatErrorType.STREAM_ERROR
    ];

    return retryableTypes.includes(error.type);
  }

  /**
   * Report error to monitoring service (placeholder for future implementation)
   */
  reportError(error: ChatError): Observable<void> {
    // In a production environment, this would send to a monitoring service
    // For now, just log to console
    console.error('ChatErrorHandler: Reporting error', {
      type: error.type,
      message: error.message,
      timestamp: new Date(error.timestamp).toISOString(),
      retryable: error.retryable,
      details: error.details
    });

    // Return completed observable
    return of(undefined);
  }

  /**
   * Extract error message from various error formats
   */
  private extractErrorMessage(error: any): string {
    // Try different error message properties
    if (typeof error === 'string') {
      return error;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.statusText) {
      return error.statusText;
    }

    if (error?.body?.message) {
      return error.body.message;
    }

    // Default message
    return 'An error occurred';
  }

  /**
   * Extract additional error details for debugging
   */
  private extractErrorDetails(error: any): any {
    const details: any = {};

    // HTTP error details
    if (error?.status !== undefined) {
      details.status = error.status;
      details.statusText = error.statusText;
      details.url = error.url;
    }

    // Error name and stack
    if (error?.name) {
      details.name = error.name;
    }

    if (error?.stack && typeof error.stack === 'string') {
      // Only include first few lines of stack trace
      details.stack = error.stack.split('\n').slice(0, 3).join('\n');
    }

    // Additional error data
    if (error?.error && typeof error.error === 'object') {
      details.errorData = error.error;
    }

    // Request details if available
    if (error?.headers) {
      details.requestId = error.headers.get('x-request-id');
    }

    return details;
  }

  /**
   * Get retry delay based on error type (in milliseconds)
   */
  getRetryDelay(error: ChatError, attemptNumber: number): number {
    const baseDelay = 1000; // 1 second

    switch (error.type) {
      case ChatErrorType.RATE_LIMIT_ERROR:
        // Exponential backoff for rate limits
        return Math.min(baseDelay * Math.pow(2, attemptNumber), 60000); // Max 1 minute
      
      case ChatErrorType.NETWORK_ERROR:
        // Linear backoff for network errors
        return Math.min(baseDelay * attemptNumber, 10000); // Max 10 seconds
      
      case ChatErrorType.STREAM_ERROR:
        // Fixed delay for stream errors
        return baseDelay;
      
      default:
        return baseDelay;
    }
  }

  /**
   * Check if error indicates user should re-authenticate
   */
  requiresReauthentication(error: ChatError): boolean {
    return error.type === ChatErrorType.AUTHENTICATION_ERROR;
  }

  /**
   * Get user action suggestion based on error
   */
  getUserActionSuggestion(error: ChatError): string | null {
    switch (error.type) {
      case ChatErrorType.AUTHENTICATION_ERROR:
        return 'Please log in again to continue.';
      
      case ChatErrorType.RATE_LIMIT_ERROR:
        return 'Please wait a few moments before trying again.';
      
      case ChatErrorType.NETWORK_ERROR:
        return 'Check your internet connection and try again.';
      
      case ChatErrorType.VALIDATION_ERROR:
        return 'Please check your input and try again.';
      
      default:
        return null;
    }
  }
}
